// FA and CF: financial statements and filings from SEC EDGAR.

import { ApiError, get } from "../api";
import { button, clear, h, link, loading } from "../dom";
import * as f from "../fmt";
import { register, type Ctx } from "../registry";
import { DataTable } from "../table";

/// A 404 from the server means "SEC has nothing for this security": say so
/// plainly instead of showing an error.
function unavailable(what: string, e: ApiError): HTMLElement {
  return h(
    "div",
    { class: "pad" },
    h("div", { class: "amber" }, `${what} not available`),
    h("div", { class: "dim" }, e.message.replace(/^not found: /, "")),
  );
}

async function fetchOr404<T>(ctx: Ctx, path: string, what: string) {
  try {
    return await get<T>(path, { s: ctx.sec! }, ctx.signal);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      clear(ctx.body, unavailable(what, e));
      ctx.panel.status("SEC EDGAR");
      return null;
    }
    throw e;
  }
}

const edgarCompany = (cik: number, type = "") =>
  `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${cik}&type=${encodeURIComponent(type)}&dateb=&owner=include&count=40`;

// --- FA ---------------------------------------------------------------------

interface Period {
  fy: number;
  /// 1-4, or 0 for a full year.
  fq: number;
  start: string;
  end: string;
}

interface Row {
  key: string;
  values: (number | null)[];
  /// Indices of values computed from longer periods (Q4 = FY − 9M).
  derived?: number[];
  tags?: string[];
}

interface View {
  periods: Period[];
  rows: Row[];
}

interface Financials {
  cik: number;
  name: string;
  taxonomy: string;
  currency: string;
  latestForm?: string;
  latestFiled?: string;
  splits: { filed: string; factor: number }[];
  annual: View | null;
  quarterly: View | null;
}

type Fmt = "money" | "eps" | "shares" | "pct" | "growth" | "x";

interface Line {
  key: string;
  label: string;
  fmt: Fmt;
  /// Totals: bright.
  bold?: boolean;
  /// Ratios under the line they describe: indented, dim.
  sub?: boolean;
  /// Computed over the trailing four quarters in the quarterly view.
  ttm?: boolean;
  help?: string;
}

const ln = (key: string, label: string, opts: Partial<Line> = {}): Line => ({ key, label, fmt: "money", ...opts });
const growth = (key: string) => ln(key, "Growth y/y", { fmt: "growth", sub: true });
const margin = (key: string, label: string, help?: string) => ln(key, label, { fmt: "pct", sub: true, help });

const L = {
  revenue: ln("revenue", "Revenue", { bold: true }),
  revenueGrowth: growth("revenueGrowth"),
  costOfRevenue: ln("costOfRevenue", "Cost of revenue"),
  grossProfit: ln("grossProfit", "Gross profit", { bold: true, help: "As reported, or revenue − cost of revenue" }),
  grossMargin: margin("grossMargin", "Gross margin"),
  rnd: ln("rnd", "Research & development"),
  sga: ln("sga", "Selling, general & admin"),
  operatingIncome: ln("operatingIncome", "Operating income", { bold: true }),
  operatingMargin: margin("operatingMargin", "Operating margin"),
  interestExpense: ln("interestExpense", "Interest expense"),
  pretaxIncome: ln("pretaxIncome", "Pretax income"),
  incomeTax: ln("incomeTax", "Income tax"),
  taxRate: margin("taxRate", "Effective tax rate", "Income tax / pretax income"),
  netIncome: ln("netIncome", "Net income", { bold: true, help: "Attributable to the parent's shareholders" }),
  netMargin: margin("netMargin", "Net margin"),
  netIncomeGrowth: growth("netIncomeGrowth"),
  epsBasic: ln("epsBasic", "EPS basic", { fmt: "eps" }),
  epsDiluted: ln("epsDiluted", "EPS diluted", { fmt: "eps", bold: true }),
  epsGrowth: growth("epsGrowth"),
  dps: ln("dps", "Dividend per share", { fmt: "eps", help: "Declared (or paid) in the period" }),
  sharesDiluted: ln("sharesDiluted", "Diluted shares, M", { fmt: "shares", help: "Weighted average over the period" }),
  ebitda: ln("ebitda", "EBITDA", { help: "Operating income + depreciation & amortization" }),
  ebitdaMargin: margin("ebitdaMargin", "EBITDA margin"),
  da: ln("da", "Depreciation & amortization"),
  sbc: ln("sbc", "Stock-based compensation"),
  cfo: ln("cfo", "Cash from operations", { bold: true }),
  capex: ln("capex", "Capital expenditure"),
  cfi: ln("cfi", "Cash from investing", { bold: true }),
  dividendsPaid: ln("dividendsPaid", "Dividends paid"),
  buybacks: ln("buybacks", "Share repurchases"),
  cff: ln("cff", "Cash from financing", { bold: true }),
  fcf: ln("fcf", "Free cash flow", { bold: true, help: "Cash from operations − capital expenditure" }),
  fcfMargin: margin("fcfMargin", "FCF margin", "Free cash flow / revenue"),
  cash: ln("cash", "Cash & equivalents"),
  shortTermInvestments: ln("shortTermInvestments", "Short-term investments"),
  receivables: ln("receivables", "Receivables"),
  inventory: ln("inventory", "Inventory"),
  currentAssets: ln("currentAssets", "Current assets", { bold: true }),
  ppe: ln("ppe", "Property, plant & equipment"),
  goodwill: ln("goodwill", "Goodwill"),
  intangibles: ln("intangibles", "Other intangibles"),
  totalAssets: ln("totalAssets", "Total assets", { bold: true }),
  accountsPayable: ln("accountsPayable", "Accounts payable"),
  shortDebt: ln("shortDebt", "Short-term debt", { help: "Including the current portion of long-term debt" }),
  currentLiabilities: ln("currentLiabilities", "Current liabilities", { bold: true }),
  longDebt: ln("longDebt", "Long-term debt"),
  totalLiabilities: ln("totalLiabilities", "Total liabilities", { bold: true }),
  equity: ln("equity", "Shareholders' equity", { bold: true }),
  totalDebt: ln("totalDebt", "Total debt"),
  netDebt: ln("netDebt", "Net debt", { help: "Total debt − cash − short-term investments" }),
  roe: ln("roe", "Return on equity", { fmt: "pct", ttm: true, help: "Net income / average shareholders' equity" }),
  roa: ln("roa", "Return on assets", { fmt: "pct", ttm: true, help: "Net income / average total assets" }),
  debtToEquity: ln("debtToEquity", "Debt / equity", { fmt: "x" }),
  currentRatio: ln("currentRatio", "Current ratio", { fmt: "x", help: "Current assets / current liabilities" }),
};

type Tab = "KEY" | "IS" | "BS" | "CF";
type Section = [title: string, lines: Line[]];

const TABS: Record<Tab, Section[]> = {
  KEY: [
    ["Income", [L.revenue, L.revenueGrowth, L.grossMargin, L.operatingIncome, L.operatingMargin, L.netIncome, L.netMargin, L.epsDiluted, L.epsGrowth, L.dps]],
    ["Cash flow", [L.cfo, L.capex, L.fcf, L.fcfMargin]],
    ["Balance sheet", [L.cash, L.totalDebt, L.netDebt, L.equity]],
    ["Returns & leverage", [L.roe, L.roa, L.debtToEquity, L.currentRatio]],
  ],
  IS: [
    [
      "Income statement",
      [
        L.revenue, L.revenueGrowth, L.costOfRevenue, L.grossProfit, L.grossMargin, L.rnd, L.sga, L.operatingIncome,
        L.operatingMargin, L.interestExpense, L.pretaxIncome, L.incomeTax, L.taxRate, L.netIncome, L.netMargin, L.netIncomeGrowth,
      ],
    ],
    ["Per share", [L.epsBasic, L.epsDiluted, L.epsGrowth, L.dps, L.sharesDiluted]],
    ["Other", [L.ebitda, L.ebitdaMargin, L.da, L.sbc]],
  ],
  BS: [
    ["Assets", [L.cash, L.shortTermInvestments, L.receivables, L.inventory, L.currentAssets, L.ppe, L.goodwill, L.intangibles, L.totalAssets]],
    ["Liabilities & equity", [L.accountsPayable, L.shortDebt, L.currentLiabilities, L.longDebt, L.totalLiabilities, L.equity]],
    ["Ratios", [L.totalDebt, L.netDebt, L.debtToEquity, L.currentRatio, L.roe, L.roa]],
  ],
  CF: [
    ["Operating", [L.netIncome, L.da, L.sbc, L.cfo]],
    ["Investing", [L.capex, L.cfi]],
    ["Financing", [L.dividendsPaid, L.buybacks, L.cff]],
    ["Free cash flow", [L.fcf, L.fcfMargin]],
  ],
};

const TAB_NAMES: [Tab, string][] = [
  ["KEY", "Key"],
  ["IS", "Income"],
  ["BS", "Balance"],
  ["CF", "Cash flow"],
];

const MONEY_KEYS = new Set(Object.values(L).filter((l) => l.fmt === "money").map((l) => l.key));

const DERIVED_TIP = "Not reported for this quarter alone: computed as full year − first three quarters, or as a year-to-date difference";

interface Scale {
  div: number;
  dp: number;
  name: string;
}

/// Thousands, millions or billions, whichever keeps the largest amount in
/// the view to at most seven digits.
function scaleFor(view: View): Scale {
  let max = 0;
  for (const r of view.rows) {
    if (MONEY_KEYS.has(r.key)) for (const v of r.values) if (v !== null) max = Math.max(max, Math.abs(v));
  }
  const div = max >= 1e13 ? 1e9 : max >= 1e6 ? 1e6 : 1e3;
  return { div, dp: max / div >= 1e4 ? 0 : 1, name: div === 1e9 ? "billions" : div === 1e6 ? "millions" : "thousands" };
}

function fmtValue(v: number, fmt: Fmt, scale: Scale, sharesDp: number): string {
  switch (fmt) {
    case "money":
      return f.fixedN(v / scale.div, scale.dp);
    case "eps":
      return f.fixedN(v, 2);
    case "shares":
      return f.fixedN(v / 1e6, sharesDp);
    case "pct":
      return f.ratio(v, 1);
    case "growth":
      return f.pct(v * 100, 1);
    case "x":
      return f.fixedN(v, 2);
  }
}

const periodLabel = (p: Period) => (p.fq ? `Q${p.fq} FY${String(p.fy).slice(-2)}` : `FY${p.fy}`);

function lineRow(l: Line, r: Row, scale: Scale, fin: Financials, quarterly: boolean): HTMLElement {
  const derived = new Set(r.derived ?? []);
  const tags = r.tags?.length ? `${fin.taxonomy}: ${r.tags.join(", ")}` : "";
  const tip = [l.help, tags].filter(Boolean).join("\n");
  const sharesMax = Math.max(0, ...r.values.map((v) => Math.abs(v ?? 0)));
  const sharesDp = sharesMax / 1e6 >= 1000 ? 0 : 1;
  const label = l.label + (quarterly && l.ttm ? " (TTM)" : "");
  return h(
    "tr",
    { class: [l.bold ? "b" : "", l.sub ? "sub" : ""].join(" ") || undefined },
    h("td", { class: "lbl", title: tip || undefined }, label),
    ...r.values.map((v, i) => {
      if (v === null) return h("td", { class: "num dim" }, f.NA);
      const color = v < 0 ? "down" : l.fmt === "growth" && v > 0 ? "up" : "";
      const est = derived.has(i);
      return h("td", { class: `num ${color}${est ? " est" : ""}`, title: est ? DERIVED_TIP : undefined }, fmtValue(v, l.fmt, scale, sharesDp));
    }),
  );
}

/// Revenue and net income as paired columns, one pair per period.
function barsRow(view: View, byKey: Map<string, Row>): HTMLElement | null {
  const rev = byKey.get("revenue")?.values;
  const ni = byKey.get("netIncome")?.values;
  if (!rev && !ni) return null;
  const max = Math.max(...[...(rev ?? []), ...(ni ?? [])].map((v) => Math.abs(v ?? 0))) || 1;
  const bar = (v: number | null | undefined, cls: string) =>
    v === null || v === undefined ? null : h("i", { class: cls, style: { height: `${Math.max(1, Math.round((Math.abs(v) / max) * 34))}px` } });
  return h(
    "tr",
    { class: "bars" },
    h("td", { class: "lbl" }, h("span", { class: "amber" }, "▮ Revenue "), h("span", { class: "up" }, "▮ Net income")),
    ...view.periods.map((_, i) => {
      const n = ni?.[i];
      return h("td", null, h("div", { class: "fabars" }, bar(rev?.[i], "rev"), bar(n, (n ?? 0) < 0 ? "neg" : "pos")));
    }),
  );
}

function faTable(fin: Financials, view: View, tab: Tab, quarterly: boolean, scale: Scale): HTMLElement {
  const byKey = new Map(view.rows.map((r) => [r.key, r]));
  const head = h(
    "tr",
    null,
    h("th", { class: "lbl" }, `${fin.currency} ${scale.name}`),
    ...view.periods.map((p) => h("th", { class: "num", title: `${p.start} to ${p.end}` }, periodLabel(p), h("br"), h("span", { class: "dim" }, p.end.slice(0, 7)))),
  );
  const body = h("tbody");
  if (tab === "KEY" || tab === "IS") {
    const bars = barsRow(view, byKey);
    if (bars) body.append(bars);
  }
  for (const [title, lines] of TABS[tab]) {
    const present = lines.filter((l) => byKey.has(l.key));
    if (!present.length) continue;
    // Two cells, so the title stays put under the sticky first column.
    body.append(h("tr", { class: "group" }, h("td", { class: "lbl" }, title), h("td", { colspan: String(view.periods.length) })));
    for (const l of present) body.append(lineRow(l, byKey.get(l.key)!, scale, fin, quarterly));
  }
  return h("table", { class: "dt fa" }, h("thead", null, head), body);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const splitText = (s: { filed: string; factor: number }) =>
  `${s.factor >= 1 ? `${s.factor}:1` : `1:${Math.round(1 / s.factor)}`} (by ${s.filed.slice(0, 7)})`;

async function runFa(ctx: Ctx): Promise<void> {
  clear(ctx.body, loading());
  const r = await fetchOr404<Financials>(ctx, "financials", "Financial statements");
  if (!r) return;
  const fin = r.data;
  let tab: Tab = (ctx.args.find((a) => a in TABS) as Tab | undefined) ?? "KEY";
  let quarterly = (ctx.args.includes("Q") && !!fin.quarterly) || !fin.annual;

  // The panel body scrolls both ways (so PgUp/PgDn work); the bars above
  // the table stay put horizontally.
  const toolbar = h("div", { class: "toolbar fa-pin" });
  const info = h("div", { class: "fa-info fa-pin dim" });
  const wrap = h("div");
  clear(ctx.body, toolbar, info, wrap);

  const buttons: [string, () => boolean, () => void, string?][] = [
    ...TAB_NAMES.map(([t, name]): [string, () => boolean, () => void] => [name, () => tab === t, () => (tab = t)]),
    ["Annual", () => !quarterly, () => (quarterly = !fin.annual), fin.annual ? undefined : "No annual statements in XBRL"],
    [
      "Quarterly",
      () => quarterly,
      () => (quarterly = !!fin.quarterly),
      fin.quarterly ? undefined : "No quarterly statements in XBRL (foreign filers usually report only annually)",
    ],
  ];
  const nums = buttons.map(([, , set]) => ctx.panel.action(() => (set(), draw())));

  const draw = () => {
    clear(
      toolbar,
      ...buttons.map(([label, on, set, why], i) => {
        const b = button("", () => (set(), draw()), on(), why);
        b.append(h("span", { class: on() ? undefined : "yellow" }, `${nums[i]}) `), label);
        if (why) b.disabled = true;
        return i === 3 ? [b, h("span", { class: "sep" })] : [b];
      }).flat(),
    );
    const view = (quarterly ? fin.quarterly : fin.annual)!;
    const scale = scaleFor(view);
    const fyEnd = fin.annual?.periods.at(-1)?.end;
    const hasDerived = view.rows.some((row) => row.derived?.length);
    clear(
      info,
      `${fin.currency} ${scale.name} except per share · ${fin.taxonomy === "ifrs-full" ? "IFRS" : "US GAAP"}`,
      fyEnd ? ` · fiscal year ends ${MONTHS[Number(fyEnd.slice(5, 7)) - 1]}` : "",
      hasDerived ? " · dotted: derived from full-year or year-to-date figures" : "",
      fin.splits.length ? ` · per share adjusted for splits ${fin.splits.map(splitText).join(", ")}` : "",
      " · ",
      link(edgarCompany(fin.cik), "EDGAR"),
    );
    info.title = info.textContent ?? "";
    clear(wrap, faTable(fin, view, tab, quarterly, scale));
    // Newest periods are on the right; start there when the panel is narrow.
    ctx.body.scrollLeft = ctx.body.scrollWidth;
  };
  draw();
  ctx.panel.status(
    `SEC EDGAR XBRL · CIK ${fin.cik}${fin.latestForm ? ` · latest ${fin.latestForm} filed ${fin.latestFiled}` : ""}`,
    r.meta,
  );
}

// --- CF ---------------------------------------------------------------------

interface Filing {
  date: string;
  form: string;
  cat: string;
  /// The filer's document description, when it says more than the form.
  desc?: string;
  /// 8-K item numbers, `2.02,9.01`.
  items?: string;
  period?: string;
  url: string;
}

interface Filings {
  cik: number;
  name: string;
  tickers: string[];
  exchanges: string[];
  sic?: string;
  state?: string;
  fiscalYearEnd?: string;
  since?: string;
  older: boolean;
  counts: Record<string, number>;
  filings: Filing[];
}

const CATS: [string, string][] = [
  ["all", "All"],
  ["annual", "10-K"],
  ["quarterly", "10-Q"],
  ["current", "8-K"],
  ["insider", "Insider"],
  ["proxy", "Proxy"],
  ["registration", "Registration"],
  ["ownership", "Ownership"],
  ["other", "Other"],
];

const CF_ARGS: Record<string, string> = {
  ALL: "all",
  "10K": "annual",
  "10-K": "annual",
  "20F": "annual",
  "20-F": "annual",
  "10Q": "quarterly",
  "10-Q": "quarterly",
  "8K": "current",
  "8-K": "current",
  "6K": "current",
  "6-K": "current",
  INSIDER: "insider",
  INS: "insider",
  PROXY: "proxy",
  REG: "registration",
  OWN: "ownership",
  OTHER: "other",
};

const FORM_NAMES: Record<string, string> = {
  "10-K": "Annual report",
  "10-KT": "Transition report",
  "20-F": "Annual report (foreign issuer)",
  "40-F": "Annual report (Canadian issuer)",
  ARS: "Annual report to shareholders",
  "10-Q": "Quarterly report",
  "8-K": "Current report",
  "6-K": "Current report (foreign issuer)",
  "3": "Initial statement of beneficial ownership",
  "4": "Change in beneficial ownership",
  "5": "Annual statement of beneficial ownership",
  "144": "Notice of proposed sale of securities",
  "DEF 14A": "Definitive proxy statement",
  DEFA14A: "Additional proxy soliciting materials",
  DEFM14A: "Definitive merger proxy",
  "PRE 14A": "Preliminary proxy statement",
  PX14A6G: "Exempt solicitation (shareholder notice)",
  PX14A6N: "Exempt solicitation",
  DFAN14A: "Proxy materials by a non-management party",
  "S-1": "Registration statement",
  "S-3": "Shelf registration statement",
  "S-3ASR": "Automatic shelf registration",
  "S-4": "Registration (business combination)",
  "S-8": "Registration (employee benefit plan)",
  "S-8 POS": "Post-effective amendment to S-8",
  "F-1": "Registration statement (foreign issuer)",
  "F-3": "Shelf registration (foreign issuer)",
  "424B2": "Prospectus supplement",
  "424B3": "Prospectus",
  "424B4": "Prospectus",
  "424B5": "Prospectus supplement",
  "424B7": "Prospectus supplement",
  "424B8": "Prospectus",
  FWP: "Free writing prospectus",
  "8-A12B": "Registration of a class of securities",
  "25-NSE": "Removal from listing",
  "SC 13G": "Passive ownership over 5%",
  "SCHEDULE 13G": "Passive ownership over 5%",
  "SC 13D": "Active ownership over 5%",
  "SCHEDULE 13D": "Active ownership over 5%",
  "13F-HR": "Institutional holdings report",
  "11-K": "Employee benefit plan annual report",
  SD: "Specialized disclosure (conflict minerals)",
  CORRESP: "Correspondence with SEC staff",
  UPLOAD: "SEC staff letter",
  "NT 10-K": "Notice of late annual report",
  "NT 10-Q": "Notice of late quarterly report",
  "N-PX": "Proxy voting record",
  IRANNOTICE: "Iran activity disclosure notice",
  CERT: "Exchange listing certification",
  EFFECT: "Notice of effectiveness",
};

const ITEMS: Record<string, string> = {
  "1.01": "Material agreement",
  "1.02": "Agreement terminated",
  "1.03": "Bankruptcy",
  "1.05": "Cybersecurity incident",
  "2.01": "Acquisition or disposition",
  "2.02": "Results of operations",
  "2.03": "New financial obligation",
  "2.04": "Obligation accelerated",
  "2.05": "Exit or restructuring costs",
  "2.06": "Impairment",
  "3.01": "Delisting notice",
  "3.02": "Unregistered equity sale",
  "3.03": "Holder rights modified",
  "4.01": "Auditor change",
  "4.02": "Non-reliance on financials",
  "5.01": "Change in control",
  "5.02": "Director or officer change",
  "5.03": "Charter or bylaw amendment",
  "5.04": "Benefit plan trading halt",
  "5.05": "Code of ethics change",
  "5.07": "Shareholder vote",
  "5.08": "Director nominations",
  "7.01": "Reg FD disclosure",
  "8.01": "Other events",
  "9.01": "Exhibits",
};

/// What a filing is. The filer's own document description is only used for
/// forms we have no name for, and to say who filed a shareholder
/// solicitation ("BOWYER RESEARCH - PX14A6G").
function describe(x: Filing): string {
  const amended = x.form.endsWith("/A");
  const base = amended ? x.form.slice(0, -2) : x.form;
  const known = FORM_NAMES[base];
  let d = known ?? x.desc ?? base;
  if (amended) d += " (amended)";
  if (x.items && (base === "8-K" || base === "6-K")) {
    const codes = x.items.split(",").map((s) => s.trim());
    const names = codes.filter((c) => c !== "9.01" || codes.length === 1).map((c) => ITEMS[c] ?? `Item ${c}`);
    if (names.length) d += `: ${names.join("; ")}`;
  } else if (known && /^(PX14|DFAN)/.test(base) && x.desc?.includes(" - ")) {
    d += ` by ${x.desc.split(" - ")[0]}`;
  }
  return d;
}

function openDoc(url: string): void {
  if (url.startsWith("https://www.sec.gov/")) window.open(url, "_blank", "noopener,noreferrer");
}

/// The form a category's button is named after, by what the company files:
/// 20-F and 6-K for foreign issuers.
function catLabel(cat: string, label: string, filings: Filing[]): string {
  if (cat !== "annual" && cat !== "current") return label;
  const counts = new Map<string, number>();
  for (const x of filings) if (x.cat === cat) counts.set(x.form.replace(/\/A$/, ""), (counts.get(x.form.replace(/\/A$/, "")) ?? 0) + 1);
  return [...counts].sort((a, b) => b[1] - a[1])[0]?.[0] ?? label;
}

async function runCf(ctx: Ctx): Promise<void> {
  clear(ctx.body, loading());
  const r = await fetchOr404<Filings>(ctx, "filings", "Filings");
  if (!r) return;
  const data = r.data;
  let cat = CF_ARGS[ctx.args[0]] ?? "all";

  const toolbar = h("div", { class: "toolbar" });
  const head = h("div", { class: "fa-info" });
  const foot = h("div", { class: "pad dim" });
  const table = new DataTable<Filing>(
    [
      { label: "Filed", get: (x) => x.date, cls: () => "nowrap" },
      { label: "Form", get: (x) => x.form, cls: () => "amber" },
      { label: "Description", get: (x) => describe(x), fmt: (v: string, x) => link(x.url, v, v), cls: () => "desc" },
      { label: "Period", get: (x) => x.period || null, cls: () => "dim" },
    ],
    { panel: ctx.panel, action: (x) => () => openDoc(x.url) },
  );

  const total = data.filings.length;
  const draw = () => {
    clear(
      toolbar,
      ...CATS.filter(([c]) => c === "all" || data.counts[c]).map(([c, label]) => {
        const n = c === "all" ? total : data.counts[c];
        return button(`${catLabel(c, label, data.filings)} ${n}`, () => ((cat = c), draw()), c === cat);
      }),
    );
    const shown = cat === "all" ? data.filings : data.filings.filter((x) => x.cat === cat);
    table.set(shown);
    const capped = cat !== "all" && data.counts[cat] > shown.length;
    const type = cat === "all" ? "" : catLabel(cat, "", data.filings);
    clear(
      foot,
      shown.length ? "" : "No filings of this kind in the period covered. ",
      capped ? `Newest ${shown.length} of ${data.counts[cat]}. ` : "",
      data.since ? `Filings since ${data.since}. ` : "",
      data.older || capped ? "Older filings: " : "Full index: ",
      link(edgarCompany(data.cik, type), "EDGAR"),
    );
  };
  clear(
    head,
    h("span", { class: "amber" }, data.name),
    h("span", { class: "dim" }, ` · CIK ${data.cik}`),
    data.sic ? h("span", { class: "dim" }, ` · ${data.sic}`) : null,
    data.state ? h("span", { class: "dim" }, ` · inc. ${data.state}`) : null,
    data.fiscalYearEnd ? h("span", { class: "dim" }, ` · FY ends ${MONTHS[Number(data.fiscalYearEnd.slice(0, 2)) - 1]} ${Number(data.fiscalYearEnd.slice(2))}`) : null,
    data.exchanges.length ? h("span", { class: "dim" }, ` · ${data.tickers.map((t, i) => `${t}${data.exchanges[i] ? ` (${data.exchanges[i]})` : ""}`).join(", ")}`) : null,
  );
  draw();
  clear(ctx.body, toolbar, head, table.el, foot);
  ctx.panel.status(`SEC EDGAR · ${total} filings`, r.meta);
}

register(
  {
    code: "FA",
    name: "Financial Analysis",
    group: "Equity",
    needsSec: true,
    args: /^(KEY|IS|BS|CF|Q|A)$/,
    help: "Income statement, balance sheet, cash flow and ratios from SEC filings. FA IS Q for quarterly income.",
    run: runFa,
  },
  {
    code: "CF",
    name: "Company Filings",
    group: "Equity",
    needsSec: true,
    args: /^(ALL|10-?K|10-?Q|8-?K|20-?F|6-?K|INS|INSIDER|PROXY|REG|OWN|OTHER)$/,
    help: "SEC filings with links to the documents. CF 10K, CF 8K, CF INSIDER.",
    run: runCf,
  },
);
